/**
 * The notification bell's live-update stream: a ticketed EventSource on
 * /api/notifications/stream with the client-owned reconnect ladder, the
 * connection-cap cooldown, the single-flight connect guard and the sync-event
 * debounce. Extracted VERBATIM from hooks/useNotifications.ts (refactor phase
 * N1, docs/refactoring/plans/2026-09-08-usenotifications.md): the refs, the
 * constants, connectSSE, scheduleReconnect and the init effect are unchanged
 * apart from two seam lines in the effect (it calls `onInit()` where the
 * monolith called its two fetches, and lists it in the deps), and the log
 * prefix stays "[useNotifications]" because those strings are what production
 * consoles and the PostHog console-error autocapture already key on.
 *
 * Behaviour is pinned by __tests__/useNotifications.stream.test.tsx (fake fetch
 * + fake EventSource + fake timers, driven through the public hook) and by the
 * source guards that now read THIS file: connectRace, connectionCap,
 * networkNoise, sseEscalation, ticketFailureNoise, and the invalidation-sites
 * guard for the three query keys.
 *
 * components/comments/useCommentStream.ts carries a twin of this ladder for
 * the comment thread; folding the two onto one hook is a separate change (see
 * the plan's "Not in scope").
 */
import { useEffect, useCallback, useRef, type Dispatch, type SetStateAction } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { AppNotification, AnnouncementItem } from '@overlayqa/shared';
import {
  isConnectionCapError,
  resolveTicketFailureDelay,
  SSE_CONNECTION_CAP_COOLDOWN_MS,
} from '@overlayqa/shared';
import { useAuth } from '../../lib/auth';
import { apiBaseUrl } from '../../lib/apiBaseUrl';
import { log } from '@/lib/logger';
import { createSseDropTracker } from '@/lib/sseDropTracker';
import { createSseConnectGuard } from '@/lib/sseConnectGuard';
import { capture } from '@/lib/analytics';
import {
  SSE_STREAM_FAILURE_EVENT,
  buildSseStreamFailureProps,
  isEscalationCrossing,
} from '@/lib/sseStreamFailure';

const API_URL = apiBaseUrl();
const MAX_NOTIFICATIONS = 100;
const RECONNECT_BASE_MS = 2000;
const RECONNECT_MAX_MS = 30000;

/**
 * Sync events invalidate query cache but are NOT stored as notifications.
 * issue_status_changed and issue_created are now stored notifications (removed from sync).
 */
const SYNC_EVENT_TYPES = new Set([
  'issues_changed',
  'issues_deleted',
]);

export interface UseNotificationStreamArgs {
  setNotifications: Dispatch<SetStateAction<AppNotification[]>>;
  setAnnouncements: Dispatch<SetStateAction<AnnouncementItem[]>>;
  /** Runs at the start of every signed-in init, before the stream connects: the composer's initial fetches. */
  onInit: () => void;
}

export function useNotificationStream({ setNotifications, setAnnouncements, onInit }: UseNotificationStreamArgs): void {
  const { getToken, isSignedIn, userId } = useAuth();
  const queryClient = useQueryClient();
  const eventSourceRef = useRef<EventSource | null>(null);
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout>>();
  const reconnectDelayRef = useRef(RECONNECT_BASE_MS);
  // Escalates repeated consecutive drops to error; reset on the server's
  // `heartbeat` event (sent immediately after a successful addConnection),
  // NOT onopen — the TOO_MANY_CONNECTIONS path 200s then closes heartbeat-less.
  const dropTrackerRef = useRef(createSseDropTracker());
  // One first-hit log + one sse_stream_failed capture per capped streak: the
  // condition is expected (the user simply has too many tabs open) and
  // self-healing, so reporting every retry would restore the noise this fix
  // removed.
  const capLoggedRef = useRef(false);
  const refreshAfterReconnectRef = useRef(onInit);
  refreshAfterReconnectRef.current = onInit;
  const syncDebounceRef = useRef<ReturnType<typeof setTimeout>>();
  const cancelledRef = useRef(false);
  // Single-flight guard for connectSSE. Held in a ref ON PURPOSE: it has to
  // outlive an effect re-run, because the attempt it supersedes is one started
  // by the PREVIOUS run. A guard recreated per run would compare every token
  // against a fresh epoch and never report anything stale, i.e. be inert.
  const connectGuardRef = useRef(createSseConnectGuard());

  const getTokenRef = useRef(getToken);
  useEffect(() => {
    getTokenRef.current = getToken;
  }, [getToken]);

  // Connect SSE stream using ticket-based auth
  const connectSSE = useCallback(async () => {
    if (cancelledRef.current) return;
    // Claim the slot before the first await. Any attempt started after this
    // one supersedes it, and the stale check below stops the superseded
    // attempt from opening a connection nothing can ever close.
    const connectToken = connectGuardRef.current.begin();

    try {
      const token = await getTokenRef.current();
      if (!token || cancelledRef.current) return;

      // Checked HERE too, not only before construction below: the close block
      // is a WRITE to the shared ref, and a superseded attempt reaching it
      // tears down the newer attempt's LIVE stream, then returns at the stale
      // check further down without scheduling a reconnect. `close()` fires no
      // error event, so nothing recovers and live notifications are dead for
      // the rest of the page session. cancelledRef cannot cover this: a later
      // effect run has already reset it to false, which is precisely how the
      // superseded attempt gets this far.
      if (connectGuardRef.current.isStale(connectToken)) return;

      // Close existing connection
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }

      // Step 1: Get single-use ticket via Bearer auth
      log.info('[useNotifications] Requesting stream ticket');
      const ticketRes = await fetch(`${API_URL}/api/notifications/stream-ticket`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      if (!ticketRes.ok) {
        // Same self-healing class as the thrown-fetch catch below: a 401 is a
        // Clerk token mid-refresh (next attempt fetches a fresh one), a 429
        // gets its own backoff just under this. Drop tracker like every
        // sibling: warn below threshold; a persistent streak stays warn too
        // (log.error mints a synthetic $exception, decision 2026-08-04) and
        // surfaces once per streak as the queryable sse_stream_failed event.
        // Incidents: single 401 filed as a fake $exception 2026-07-21; 429
        // pair 2026-07-05.
        if (dropTrackerRef.current.recordDrop() === 'error') {
          const drops = dropTrackerRef.current.consecutiveDrops();
          log.warn('[useNotifications] Stream ticket failing repeatedly', {
            status: ticketRes.status,
            consecutiveDrops: drops,
          });
          if (isEscalationCrossing(drops)) {
            capture(
              SSE_STREAM_FAILURE_EVENT,
              buildSseStreamFailureProps('notifications', 'ticket_failing', {
                consecutiveDrops: drops,
                status: ticketRes.status,
              }),
            );
          }
        } else {
          log.warn('[useNotifications] Failed to get stream ticket', { status: ticketRes.status });
        }
        // A 429 means we are already rate-limited: jump to the max delay rather
        // than retrying on the base 2s cadence and adding to the storm.
        reconnectDelayRef.current = resolveTicketFailureDelay(
          ticketRes.status,
          reconnectDelayRef.current,
          RECONNECT_MAX_MS,
        );
        scheduleReconnect();
        return;
      }
      const ticketBody = (await ticketRes.json()) as { data?: { ticket?: string } };
      const ticket = ticketBody.data?.ticket;
      if (!ticket) {
        log.error('[useNotifications] Missing ticket in response');
        scheduleReconnect();
        return;
      }

      if (cancelledRef.current) return;
      // Checked HERE, before construction, not after: `new EventSource` opens
      // the connection immediately, so a stale attempt that constructs first
      // and checks second has already leaked the thing we are preventing.
      // cancelledRef alone is not enough, since a later effect run resets it
      // to false while this attempt is still resuming.
      if (connectGuardRef.current.isStale(connectToken)) return;

      // Step 2: Open EventSource with ticket (not raw JWT)
      const url = `${API_URL}/api/notifications/stream?ticket=${encodeURIComponent(ticket)}`;
      const es = new EventSource(url);
      eventSourceRef.current = es;
      log.info('[useNotifications] Opening SSE with ticket');

      es.addEventListener('inbox_changed', (e) => {
        if (cancelledRef.current || connectGuardRef.current.isStale(connectToken)) return;
        try {
          JSON.parse(e.data);
          setNotifications(current => current);
          setAnnouncements(current => current);
          refreshAfterReconnectRef.current();
        } catch (error) { log.warn('[useNotifications] Invalid inbox update', { error: String(error) }); }
      });
      es.addEventListener('notification', (e) => {
        if (cancelledRef.current || connectGuardRef.current.isStale(connectToken)) return;
        try {
          const parsed = JSON.parse(e.data);
          log.info('[useNotifications] SSE event received', { type: parsed.type, id: parsed.id });

          // Sync events: invalidate query cache, don't store as notification
          if (SYNC_EVENT_TYPES.has(parsed.type)) {
            log.info('[useNotifications] Sync event, invalidating cache', { type: parsed.type });
            if (syncDebounceRef.current) clearTimeout(syncDebounceRef.current);
            syncDebounceRef.current = setTimeout(() => {
              queryClient.invalidateQueries({ queryKey: ['issues'] });
              queryClient.invalidateQueries({ queryKey: ['dashboard-summary'] });
              queryClient.invalidateQueries({ queryKey: ['project-summary'] });
            }, 2000);
            return;
          }

          // Cross-surface read sync events
          if (parsed.type === 'notification_read') {
            setNotifications((prev) =>
              prev.map((n) => n.id === parsed.notificationId ? { ...n, isRead: true } : n)
            );
            return;
          }
          if (parsed.type === 'announcement_read') {
            setAnnouncements((prev) =>
              prev.map((a) => a.id === parsed.announcementId ? { ...a, isRead: true } : a)
            );
            return;
          }

          // Stored notifications: add to state
          const notification: AppNotification = parsed;
          log.info('[useNotifications] SSE notification received', { id: notification.id, type: notification.type });
          setNotifications((prev) => {
            // Dedupe by id
            if (prev.some((n) => n.id === notification.id)) return prev;
            return [notification, ...prev].slice(0, MAX_NOTIFICATIONS);
          });
        } catch (err) {
          log.error('[useNotifications] SSE parse error', {
            error: err instanceof Error ? err.message : String(err),
          });
        }
      });

      es.addEventListener('heartbeat', () => {
        // routes/notifications.ts:114 sends this only after subscribing. The
        // server does not replay missed events, so reconcile after an outage,
        // once per recovered connection rather than on every heartbeat.
        const needsCatchUp = capLoggedRef.current || dropTrackerRef.current.consecutiveDrops() > 0;
        reconnectDelayRef.current = RECONNECT_BASE_MS;
        dropTrackerRef.current.recordConnected();
        capLoggedRef.current = false;
        if (needsCatchUp) {
          refreshAfterReconnectRef.current();
          queryClient.invalidateQueries({ queryKey: ['issues'] });
          queryClient.invalidateQueries({ queryKey: ['dashboard-summary'] });
          queryClient.invalidateQueries({ queryKey: ['project-summary'] });
        }
      });

      es.onerror = (event) => {
        // The server rejects past MAX_CONNECTIONS_PER_USER by responding
        // 200, emitting this `error` event with a code, then closing without a
        // heartbeat. Retrying on the normal cadence just burns the per-user SSE
        // rate-limit bucket; the cap clears on its own when another tab closes.
        if (isConnectionCapError(event)) {
          if (capLoggedRef.current) {
            log.warn('[useNotifications] SSE still at the connection limit, waiting out the cooldown');
          } else {
            capLoggedRef.current = true;
            // warn + queryable capture, NOT error: the cap is a handled,
            // self-recovering condition (since 2026-08-04 the server evicts
            // the user's oldest stream rather than refusing the newest), and
            // log.error filed it as a fake $exception (row 2026-08-01).
            log.warn('[useNotifications] SSE connection limit reached', {
              cooldownMs: SSE_CONNECTION_CAP_COOLDOWN_MS,
            });
            capture(
              SSE_STREAM_FAILURE_EVENT,
              buildSseStreamFailureProps('notifications', 'connection_cap'),
            );
          }
          es.close();
          eventSourceRef.current = null;
          scheduleReconnect(SSE_CONNECTION_CAP_COOLDOWN_MS);
          return;
        }

        // Routine drops (the upstream ~15-min max-connection-duration recycles
        // every stream) stay at warn; a stream that repeatedly never reaches a
        // subscribed state (no heartbeat between drops) escalates to a warn
        // with the count + one queryable sse_stream_failed capture per streak
        // (log.error mints a synthetic $exception, decision 2026-08-04).
        if (dropTrackerRef.current.recordDrop() === 'error') {
          const drops = dropTrackerRef.current.consecutiveDrops();
          log.warn('[useNotifications] SSE connection failing repeatedly', {
            consecutiveDrops: drops,
          });
          if (isEscalationCrossing(drops)) {
            capture(
              SSE_STREAM_FAILURE_EVENT,
              buildSseStreamFailureProps('notifications', 'stream_failing', {
                consecutiveDrops: drops,
              }),
            );
          }
        } else {
          log.warn('[useNotifications] SSE error, scheduling reconnect');
        }
        es.close();
        eventSourceRef.current = null;
        scheduleReconnect();
      };

      es.onopen = () => {
        log.info('[useNotifications] SSE connected');
      };
    } catch (err) {
      // A thrown ticket fetch (server unreachable) is a network blip in the SAME
      // self-healing class as an es.onerror drop: scheduleReconnect recovers it,
      // and an offline tab retries on the 2s..30s ladder — so an unconditional
      // error would mint a $exception per retry (incident 2026-07-10). Route it
      // through the shared drop tracker, exactly like onerror: warn below the
      // threshold; a persistent streak stays warn (the error-level escalation
      // itself minted fake $exceptions, PostHog issue 019ef960, decision
      // 2026-08-04) and captures sse_stream_failed once per streak.
      if (dropTrackerRef.current.recordDrop() === 'error') {
        const drops = dropTrackerRef.current.consecutiveDrops();
        log.warn('[useNotifications] SSE connect failing repeatedly', {
          consecutiveDrops: drops,
          error: err instanceof Error ? err.message : String(err),
        });
        if (isEscalationCrossing(drops)) {
          capture(
            SSE_STREAM_FAILURE_EVENT,
            buildSseStreamFailureProps('notifications', 'connect_failing', {
              consecutiveDrops: drops,
              error: err,
            }),
          );
        }
      } else {
        log.warn('[useNotifications] SSE connect failed, scheduling reconnect', {
          error: err instanceof Error ? err.message : String(err),
        });
      }
      scheduleReconnect();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryClient]);

  /**
   * @param explicitDelayMs When set, reconnect after exactly this delay and HOLD
   * it (no doubling, no decay) until a heartbeat confirms a real subscription.
   * Used for the connection-cap cooldown, which must not tighten on repeated
   * rejections nor decay back toward RECONNECT_MAX_MS.
   */
  const scheduleReconnect = useCallback((explicitDelayMs?: number) => {
    if (cancelledRef.current) return;
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
    }
    const delay = explicitDelayMs ?? reconnectDelayRef.current;
    reconnectDelayRef.current = explicitDelayMs ?? Math.min(delay * 2, RECONNECT_MAX_MS);
    log.info('[useNotifications] Reconnecting in', { delayMs: delay });
    reconnectTimeoutRef.current = setTimeout(() => {
      connectSSE();
    }, delay);
  }, [connectSSE]);

  // Init: fetch + connect SSE when signed in
  useEffect(() => {
    if (!isSignedIn) return;

    cancelledRef.current = false;
    onInit();
    connectSSE();

    return () => {
      cancelledRef.current = true;
      // Supersede anything still in flight. Without this, an attempt awaiting
      // its ticket right now would resume after the next run resets
      // cancelledRef to false and open a stream this cleanup cannot see.
      connectGuardRef.current.invalidate();
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      if (syncDebounceRef.current) {
        clearTimeout(syncDebounceRef.current);
      }
    };
  }, [isSignedIn, userId, onInit, connectSSE]);
}
